import { PORTFOLIO, type PortfolioProject } from "./portfolio";

export type CaseStudySection = {
  eyebrow: string;
  heading: string;
  body: string[];
  bullets?: string[];
};

export type CaseStudyFAQ = { q: string; a: string };

export type CaseStudy = {
  slug: string;
  project: PortfolioProject;
  hero: {
    eyebrow: string;
    headline: string;
    deck: string;
  };
  executiveSummary: string[];
  sections: CaseStudySection[];
  quote?: { text: string; attribution: string; role: string };
  outcome: string[];
  readingTime: string;
  /** Optional — not yet wired into the case-study route template. */
  faqs?: CaseStudyFAQ[];
};

const findProject = (slug: string) => {
  const p = PORTFOLIO.find((x) => x.slug === slug);
  if (!p) throw new Error(`Portfolio project not found: ${slug}`);
  return p;
};

export const CASE_STUDIES: CaseStudy[] = [
  {
    slug: "foreign-affairs-auto",
    project: findProject("foreign-affairs-auto"),
    hero: {
      eyebrow: "Automotive · Technical SEO",
      headline: "From legacy site debt to 6.1K monthly organic visits",
      deck:
        "How we consolidated a 40-year-old European auto specialist's technical stack and rebuilt authority to compound organic traffic across 7,000 ranking keywords.",
    },
    executiveSummary: [
      "Foreign Affairs Auto — a South Florida European auto specialist founded in 1982 — had strong brand recognition but was losing organic share to newer competitors with cleaner technical foundations.",
      "Four decades of incremental site changes had left behind a tangle of duplicate service pages, inconsistent canonicals, and internal linking that rewarded nothing in particular. The brand equity was real; the crawl graph didn't reflect it.",
      "Over six months we ran a full technical audit, consolidated duplicate service pages, rebuilt internal linking around brand + service intent, and executed a white-hat digital PR programme built specifically to land on URLs that could actually rank.",
      "The site now attracts 6.1K organic visitors per month across 7K ranking keywords, backed by 911 referring domains — a defensible authority moat in a competitive niche that would take a well-funded competitor years, and six figures, to replicate.",
      "This case study walks through the discovery findings, the technical remediation sequence, the information architecture rebuild, the content and PR programme, and the measurement framework that let us prove causality rather than correlation.",
    ],
    sections: [
      {
        eyebrow: "Discovery & audit",
        heading: "What a 40-year crawl history actually looks like",
        body: [
          "We opened the engagement with a full-site crawl (Screaming Frog + GSC coverage export + Semrush Site Audit, cross-referenced for false positives) rather than trusting any single tool's summary score. The crawl surfaced 147 discrete technical issues, which we triaged into a fix log with owner, priority, and estimated ranking impact.",
          "The headline finding: six near-identical brand verticals — BMW, Mercedes, Audi, Porsche, MINI, and Land Rover repair — each had duplicated service pages (brake repair, transmission, diagnostics) with 80%+ text overlap. Google was folding most of these into a handful of indexed URLs and ignoring the rest, which meant half the site's content investment was earning zero ranking credit.",
          "Beneath that: inconsistent hreflang for the practice's meaningful Spanish-speaking client base, a redirect chain three hops deep from an old CMS migration, and mobile LCP over 4.0 seconds on the pages that mattered most commercially.",
        ],
        bullets: [
          "Duplicate service pages diluting ranking signals",
          "Slow LCP (>4.0s on mobile) hurting Core Web Vitals",
          "Weak internal linking between brand pages and services",
          "PR mentions landing on redirected or canonicalised URLs",
          "Inconsistent hreflang for the Spanish-speaking audience segment",
        ],
      },
      {
        eyebrow: "Technical remediation",
        heading: "Fixing the foundation before touching authority",
        body: [
          "We refuse to build links onto a broken foundation — link equity flowing into a canonicalised or redirected URL is link equity wasted. So month one was audit-and-fix only, with no outward-facing PR or content work, against a rule that every fix needed a before/after crawl diff to close it out.",
          "The consolidation itself merged the six duplicate brand-service matrices into a single templated system: one canonical service page per repair type, with brand-specific modules (parts sourcing, OEM diagnostic tooling, warranty handling) injected per brand rather than duplicated as standalone pages. This alone recovered indexation on roughly 40 previously-orphaned URLs.",
          "LCP was addressed through image pipeline changes (responsive AVIF/WebP, correctly sized for viewport), deferred non-critical JS, and font-display swapping — taking the mobile LCP on core service pages from over 4.0s to a consistently sub-2.5s pass in field data.",
        ],
      },
      {
        eyebrow: "Information architecture",
        heading: "A hub-and-spoke model built for how people actually search",
        body: [
          "Once the crawl was clean, we rebuilt the internal-linking model around a hub-and-spoke: brand hubs (BMW repair, Mercedes repair) linking down to service spokes (transmission, brake, diagnostics), and every spoke linking back up to the relevant location page — closing the loop that generic navigation had been missing.",
          "This mattered because the site's actual search demand splits cleanly into two intents: brand-qualified ('BMW transmission repair Miami') and service-qualified ('transmission repair near me'). The old flat architecture served neither well; the hub-and-spoke model let each page compete for the query cluster it was actually built to answer.",
        ],
      },
      {
        eyebrow: "Content programme",
        heading: "Writing for the gap, not for the word count",
        body: [
          "Content briefs were built from a Semrush + GSC gap analysis rather than a generic content calendar — we looked at what the site was already ranking position 8–20 for and wrote to close that gap specifically, since that's the fastest-moving traffic band in an established site.",
          "Every new or rewritten service page included a diagnostic-specific FAQ block, sourced from actual service-desk questions the shop fields daily, which doubled as both user-value content and FAQPage schema targets.",
        ],
      },
      {
        eyebrow: "Digital PR & link acquisition",
        heading: "Only launching PR once there was something worth linking to",
        body: [
          "Only once the crawl was clean did the digital PR programme launch — targeting automotive trade publications, local Miami business press, and enthusiast community forums where European car owners already congregate and trust recommendations.",
          "Twelve high-authority placements were secured over the engagement window, each pointed at a canonical, indexable service or brand hub — not a homepage blast, which is the single most common mistake in agency-run PR campaigns. Anchor text was varied naturally across brand, service, and branded-navigational terms.",
          "The result compounded because referring-domain growth landed on pages already structured to convert that authority into rankings, rather than pages that would need a second remediation pass six months later.",
        ],
        bullets: [
          "12 high-authority PR placements across trade + local press",
          "Anchor text distributed across brand, service, and navigational terms",
          "Every placement pointed to a canonical, indexable target URL",
        ],
      },
      {
        eyebrow: "Local visibility",
        heading: "Location signals in a multi-city service area",
        body: [
          "Because Foreign Affairs Auto serves a wider South Florida catchment than a single storefront address suggests, we built out location-specific service pages with unique local proof points (turnaround time, loaner-car availability, local landmark directions) rather than templated city-swaps.",
          "AutoRepair, LocalBusiness, and FAQPage schema were deployed across the full page set so that rich results and, increasingly, AI-search citations had structured data to draw from rather than having to infer entity relationships from prose.",
        ],
      },
      {
        eyebrow: "Measurement setup",
        heading: "Every number in this report is sourced and dated",
        body: [
          "We instrumented the engagement against three sources — Semrush for keyword and authority tracking, Google Search Console for click and impression ground truth, and GA4 for on-site behaviour — reconciled monthly so no single tool's blind spots could distort the read.",
          "A weekly technical monitor (crawl diff + Core Web Vitals field data) ran throughout the engagement so regressions were caught inside a sprint, not discovered three months later in a quarterly report.",
        ],
      },
      {
        eyebrow: "The framework",
        heading: "OPTIMIZE applied end-to-end",
        body: [
          "This engagement is a textbook SEO Growth Process execution: Observe (audit), Plan (fix log + IA), Test (staged rollouts), Iterate (weekly CWV monitoring), Measure (Semrush + GSC + GA4), Improve (content refresh cycle), Zoom (topical expansion), Elevate (authority campaign).",
        ],
        bullets: [
          "Observe: 147 technical issues found and triaged",
          "Plan: 6-month roadmap with weekly sprints",
          "Iterate: monthly ranking + CWV reviews",
          "Elevate: 12 high-authority PR placements",
        ],
      },
      {
        eyebrow: "What we'd do differently",
        heading: "Honest retrospective",
        body: [
          "In hindsight, the hreflang consolidation should have been a week-one fix rather than a month-two one — it was deprioritised behind duplicate-content cleanup because its estimated ranking impact looked smaller in isolation, but it was blocking a meaningful slice of Spanish-language local search demand the whole time.",
          "We'd also start the digital PR outreach relationship-building (without live pitching) a few weeks earlier in parallel with the technical fix window, since trade press relationships take longer to warm than the technical work takes to complete.",
        ],
      },
      {
        eyebrow: "Timeline",
        heading: "Six months, month by month",
        body: [
          "Month 1: full technical audit, fix log, and no outward campaign activity — foundation only.",
          "Month 2: duplicate-page consolidation, hreflang fix, LCP remediation, and IA rebuild begins.",
          "Month 3: hub-and-spoke internal linking ships across all brand and service verticals; content gap analysis completed.",
          "Months 4–5: content programme in production; digital PR outreach begins and lands first placements; schema deployed sitewide.",
          "Month 6: PR placements complete at 12; full measurement reconciliation; roadmap handed off for the next growth phase.",
        ],
      },
      {
        eyebrow: "What this means for you",
        heading: "If your business looks like this one",
        body: [
          "If you're running an established local or regional service business with real brand equity but flat or declining organic traffic, the diagnosis is often the same: technical debt is capping what your content and authority can achieve, and no amount of new content will fix a duplicate-content or crawl problem underneath it.",
          "The sequencing matters more than the individual tactics — audit and fix first, rebuild architecture second, then layer content and authority on a foundation that can actually convert them into rankings. Skipping the order is the most common reason SEO investments underperform.",
        ],
      },
    ],
    quote: {
      text:
        "They took a 40-year-old website and made it feel like a modern product. The traffic followed.",
      attribution: "Marketing Lead",
      role: "Foreign Affairs Auto",
    },
    outcome: [
      "6.1K monthly organic visits",
      "7K ranking keywords",
      "911 referring domains",
      "Authority Score 29 (Good)",
    ],
    readingTime: "12 min read",
  },
  {
    slug: "lynx-systems",
    project: findProject("lynx-systems"),
    hero: {
      eyebrow: "Security & Automation · Technical + Local SEO",
      headline: "619K impressions in 6 months for a security integrator",
      deck:
        "Fixing indexation, rebuilding on-page targeting, and layering local SEO turned a technically strong integrator into a compounding organic channel.",
    },
    executiveSummary: [
      "Lynx Systems is a commercial security and automation integrator with deep technical expertise but a website that was failing to rank on its own strengths — a common pattern with engineering-led businesses where the product knowledge never reaches the page in a form Google (or a buyer) can parse.",
      "The starting-point symptom was a familiar one: impressions were climbing, but clicks were flat. That's the classic signature of ranking on the wrong queries — informational, low-intent, or simply mismatched to what the business sells — while losing the commercial queries that convert.",
      "In six months we ran a full technical audit, rebuilt on-page targeting on every service page, deployed LocalBusiness and Service schema, and overhauled the Google Business Profile from the ground up.",
      "The site now generates 619K impressions and 2.27K clicks over the last six months — a +16% click lift over the prior period, with average position improving to 17.3 and still climbing as the newer content matures.",
    ],
    sections: [
      {
        eyebrow: "Discovery & audit",
        heading: "High expertise, low visibility",
        body: [
          "Lynx's engineers write world-class documentation for their own clients — spec sheets, install guides, compliance notes. None of that expertise was reaching the public-facing site in a search-friendly form. Instead, service pages leaned on generic industry language that competed with every other integrator's generic industry language.",
          "The audit found a second, more mechanical problem underneath the content issue: significant JavaScript rendering gaps meant Googlebot was seeing a stripped-down version of key pages, missing headings and body copy that only rendered client-side. Impressions were creeping up on the thin server-rendered shell; clicks stayed flat because the shell didn't say anything compelling.",
          "We also found weak internal linking between the industry pages (commercial, education, healthcare, government) and the service pages (access control, surveillance, fire alarm) that should have been feeding each other authority and context.",
        ],
      },
      {
        eyebrow: "Technical remediation",
        heading: "Fixing what Googlebot could actually see",
        body: [
          "We ran the same audit-first playbook we run on every engagement: crawl, index coverage, JS rendering diff (rendered vs raw HTML), canonicals, internal links, sitemap accuracy. Every fix logged with an owner and priority so nothing sat in a backlog undone.",
          "Sixty-two distinct technical issues were resolved, the largest cluster being render-blocking scripts that delayed content painting past the point Googlebot's rendering budget typically allows on a crawl pass.",
        ],
        bullets: [
          "Technical fix log: 62 issues resolved",
          "JS rendering diff run across all commercial templates",
          "Sitemap + canonical accuracy pass",
        ],
      },
      {
        eyebrow: "On-page rewrite",
        heading: "From generic industry language to commercial intent",
        body: [
          "We rewrote every service page with commercial intent — access control, commercial surveillance, fire alarm systems — with clear entity signalling (what the service is, who it's for, what compliance standards it meets) and internal links back to the industry pages that actually convert.",
          "This is where the engineering documentation paid off: once we had permission to mine it for plain-language explanations, the pages stopped reading like brochures and started reading like the answer to a specific buyer's specific question — which is exactly the signal both users and ranking systems reward.",
        ],
        bullets: [
          "Rewrote 18 service + industry pages",
          "Entity-first structure: what, who-for, compliance standard",
          "Internal links rebuilt between industry and service verticals",
        ],
      },
      {
        eyebrow: "Local visibility & schema",
        heading: "Local + schema: making the entity legible",
        body: [
          "LocalBusiness and Service schema went onto every relevant page, giving both search engines and AI-answer surfaces a structured description of what Lynx does and where it operates, rather than requiring inference from unstructured prose.",
          "In parallel, we ran a full Google Business Profile overhaul: category audit against three primary categories, twelve distinct services listed with descriptions, defined service areas, and a weekly posting cadence that kept the profile active rather than dormant.",
        ],
        bullets: [
          "LocalBusiness + Service schema on 24 pages",
          "GBP: 3 categories, 12 services, weekly posts",
        ],
      },
      {
        eyebrow: "Content programme",
        heading: "Turning documentation into search assets",
        body: [
          "Beyond the core service page rewrites, we built a smaller content programme around compliance and buyer-education topics — fire code requirements, access control system selection criteria — the kind of question a facilities manager searches before they're ready to talk to a salesperson.",
          "This content was deliberately structured with direct-answer paragraphs near the top, since it does double duty: ranking for informational queries and increasingly surfacing in AI-generated answers where citation-friendly structure matters as much as authority.",
        ],
      },
      {
        eyebrow: "Digital PR & authority",
        heading: "Selective authority building",
        body: [
          "Unlike a consumer brand, a B2B security integrator's link opportunities are narrower — trade associations, industry directories, and genuine case-study placements with the facilities and security publications the buyer persona actually reads. We prioritised quality and topical relevance over volume here, since irrelevant link velocity is a liability in a niche this specific.",
        ],
      },
      {
        eyebrow: "Measurement setup",
        heading: "Reading impressions and clicks together, not separately",
        body: [
          "Google Search Console was the primary source of truth for this engagement because the client's core question — are we ranking for the queries that convert — is best answered by GSC's query-level click and impression data rather than a third-party rank tracker's proxy metrics.",
          "We built a monthly reconciliation that split queries into commercial vs informational buckets, so the team could see clearly that click growth was coming from the commercial bucket the rewrite targeted, not from incidental informational traffic.",
        ],
      },
      {
        eyebrow: "What we'd do differently",
        heading: "Honest retrospective",
        body: [
          "The JS rendering fix should have shipped in week one rather than being sequenced behind the full audit — it was the single highest-leverage fix in the entire engagement, and every week it sat unfixed cost the site rendering budget on every subsequent crawl.",
          "We'd also start the GBP category audit earlier; it turned out categories were misconfigured in a way that was actively suppressing local pack visibility, and catching that in month one rather than month two would have compounded an extra month of local impressions.",
        ],
      },
      {
        eyebrow: "Timeline",
        heading: "Six months, month by month",
        body: [
          "Month 1: technical audit, JS rendering diagnosis, and fix-log triage.",
          "Month 2: rendering fixes ship; sitemap and canonical cleanup complete.",
          "Month 3: on-page rewrites begin on highest-traffic service pages first.",
          "Month 4: remaining service + industry pages rewritten; schema deployment begins.",
          "Month 5: GBP overhaul complete; weekly posting cadence begins; compliance content published.",
          "Month 6: full measurement reconciliation; +16% click lift confirmed against the prior six-month period.",
        ],
      },
      {
        eyebrow: "The result",
        heading: "Impressions that convert",
        body: [
          "Clicks grew +16% over the previous 6-month period on 619K impressions. Average position is now 17.3 — the top of page 2 and climbing, with the CTR curve trending in the right direction as the site earns more page-1 features on the commercial queries it now actually targets.",
        ],
      },
      {
        eyebrow: "What this means for you",
        heading: "If your business looks like this one",
        body: [
          "If your organic impressions are healthy but clicks aren't following, don't reach for more content first — check whether Googlebot can actually see what you've already written, and whether that content targets the commercial query, not the adjacent informational one. Technical visibility and on-page intent alignment usually solve more of the problem than volume.",
        ],
      },
    ],
    outcome: [
      "2.27K clicks (6 mo)",
      "619K impressions",
      "+16% click growth",
      "Avg. position 17.3",
    ],
    readingTime: "11 min read",
  },
  {
    slug: "air-tech-pro",
    project: findProject("air-tech-pro"),
    hero: {
      eyebrow: "HVAC · Local Services SEO",
      headline: "+78% clicks in a hyper-competitive local HVAC market",
      deck:
        "Service-area pages, a Google Business Profile overhaul, and technical CWV fixes drove 60.8K impressions and a 78% lift in qualified clicks.",
    },
    executiveSummary: [
      "Air Tech Pro HVAC operates in one of the most competitive local services categories in North America — head-to-head with national brands running six-figure paid budgets and franchise networks with decades of review volume.",
      "In three months we shipped a service-area page set, overhauled the Google Business Profile, and fixed Core Web Vitals — and generated 60.8K impressions and a 78% lift in clicks against a compressed timeline that left no room for slow iteration.",
      "This is what local SEO looks like when it's done as a system rather than a checklist: page architecture, profile optimisation, and technical performance shipped in parallel, each reinforcing the others rather than competing for priority.",
    ],
    sections: [
      {
        eyebrow: "Discovery & audit",
        heading: "The economics of local services SEO",
        body: [
          "The category is brutal by design. National HVAC brands buy visibility outright through paid search and local services ads; small operators either compete on organic plus the local map pack, or they lose the qualified-enquiry pipeline entirely to competitors with paid budgets they can't match.",
          "Air Tech Pro had a technically decent site — reasonable load times, no glaring crawl errors — but no service-area page set, thin content on core services, and inconsistent local pack visibility that swung week to week without a clear pattern the team could explain.",
          "The audit also flagged mobile usability friction on the contact and quote-request flow — the exact moment a local-services visitor decides whether to call or bounce to the next result.",
        ],
      },
      {
        eyebrow: "Information architecture",
        heading: "Service-area pages built for how HVAC is actually searched",
        body: [
          "We built the service-area page set first — HVAC repair, AC installation, furnace repair, each by city — because that's what wins both the local pack and the long-tail 'service + city' search pattern that dominates this category's query volume.",
          "Twelve cities across four services meant 48 distinct pages, each with genuinely differentiated local content (service radius, typical response time, neighbourhood-specific notes) rather than a templated city-name swap, which search engines increasingly discount as thin duplicate content.",
        ],
        bullets: [
          "Service-area page set (12 cities × 4 services)",
          "Unique local content per page, not templated swaps",
        ],
      },
      {
        eyebrow: "Local visibility",
        heading: "The Google Business Profile as a growth lever, not an afterthought",
        body: [
          "In parallel, the GBP got rebuilt from the ground up: categories re-audited against competitor overlap, services list expanded and matched to actual search demand, service areas configured properly, geo-tagged photos uploaded on a schedule, a structured review-generation flow put in place, and weekly posts to keep the profile signalling activity.",
          "For a local-services business, the GBP is frequently a bigger growth lever than the website itself, because the local pack sits above organic results for the exact commercial queries ('AC repair near me') that drive phone calls. Treating it as a one-time setup task rather than an ongoing programme is the single biggest missed opportunity we see in this category.",
        ],
        bullets: [
          "GBP overhaul + review generation",
          "Weekly posting cadence",
          "Geo-tagged photo schedule",
        ],
      },
      {
        eyebrow: "Technical remediation",
        heading: "Core Web Vitals and the quote-request funnel",
        body: [
          "Technical work ran in the background of the page-build and GBP work: mobile usability fixes on tap targets and viewport scaling, LCP improvements on the service pages carrying the most paid-search-equivalent value, and HVACBusiness plus FAQPage schema deployed on every service page.",
          "We prioritised CWV fixes on the pages feeding the quote-request form specifically, since a slow-loading contact flow on mobile — where most of this category's search traffic originates — is a direct leak in the conversion funnel that no amount of ranking improvement can fix downstream.",
        ],
        bullets: [
          "HVACBusiness + FAQPage schema",
          "LCP improvements + mobile CWV pass",
          "Quote-request flow prioritised for mobile fixes",
        ],
      },
      {
        eyebrow: "Content programme",
        heading: "Seasonal content matched to the buying calendar",
        body: [
          "HVAC demand is sharply seasonal, so the content calendar was built around the buying calendar rather than a generic evergreen list: pre-summer AC tune-up content, winter furnace-readiness content, and indoor air quality content that performs year-round and captures a broader, less price-sensitive audience.",
        ],
      },
      {
        eyebrow: "Measurement setup",
        heading: "Reading clicks and position together in a tight window",
        body: [
          "With only a three-month window, we set up weekly rather than monthly GSC reviews so any early signal — positive or negative — could be acted on inside the engagement rather than surfacing in a wrap-up report too late to matter.",
          "Impressions and clicks were tracked separately for the new service-area pages versus the existing site, which let us isolate exactly how much of the click growth was attributable to the new page set versus general site momentum.",
        ],
      },
      {
        eyebrow: "What we'd do differently",
        heading: "Honest retrospective",
        body: [
          "Given more runway, we'd have staged the service-area page rollout to launch a smaller batch first and validate the local content pattern before committing to all 48 pages at once — it worked, but a phased approach would have de-risked the investment.",
          "We'd also start the review-generation flow in week one rather than as part of the broader GBP overhaul; review velocity is one of the slower-moving local ranking factors, so every week of delay there is a week of compounding lost.",
        ],
      },
      {
        eyebrow: "Timeline",
        heading: "Three months, sprint by sprint",
        body: [
          "Weeks 1–2: audit, GBP category re-audit, and mobile usability triage.",
          "Weeks 3–6: service-area page set built and published in staged batches; CWV fixes shipped in parallel.",
          "Weeks 7–9: GBP overhaul complete, review-generation flow live, weekly posting begins, schema deployed.",
          "Weeks 10–12: seasonal content published; weekly GSC reviews confirm the click and impression trend; final reconciliation.",
        ],
      },
      {
        eyebrow: "The result",
        heading: "Clicks up 78% in a market that punishes complacency",
        body: [
          "Clicks grew from 61 to 109 (+78%) on 60.8K impressions. Average position is 27.7 and improving — every point of position gained in this category compounds because the CTR curve is steep at the top, meaning small position gains translate into outsized click gains near page one.",
        ],
      },
      {
        eyebrow: "What this means for you",
        heading: "If your business looks like this one",
        body: [
          "If you're a local-services business competing against national or franchise brands with paid budgets you can't match, the organic and local-pack lane is still winnable — but only if the service-area architecture, the Google Business Profile, and the mobile conversion funnel are all treated as one connected system rather than three separate line items on a proposal.",
        ],
      },
    ],
    outcome: [
      "+78% clicks (109 vs 61)",
      "60.8K impressions",
      "+8% impressions QoQ",
      "Avg. position 27.7",
    ],
    readingTime: "10 min read",
  },
  {
    slug: "carage-auto",
    project: findProject("carage-auto"),
    hero: {
      eyebrow: "Automotive · Local + On-Page SEO",
      headline: "From a cold start to 2.6K ranking keywords in six months",
      deck:
        "Rebuilt information architecture, on-page SEO, schema, and GBP turned a low-authority auto repair brand into a compounding organic channel with 1.9K monthly visits.",
    },
    executiveSummary: [
      "Carage Auto came to us with a functional site but zero organic momentum: no schema, no local SEO, no measurable ranking traction in a saturated automotive services niche where established competitors had a decade's head start on authority.",
      "A true cold-start engagement carries a specific kind of risk: there's no existing traffic to protect, but also no existing signal to learn from, so every early decision is made on category research and competitive gap analysis rather than the site's own historical data.",
      "Six months later the site ranks for 2.6K keywords, generates 1.9K monthly organic visits, and — notably — is cited across 137 AI-search surfaces via Semrush's AI Search tracking, a dividend of the entity-first, citation-friendly structure we built in from day one.",
      "The playbook: IA rebuild → on-page SEO → schema → GBP → Core Web Vitals — executed with weekly discipline against a Semrush-driven gap analysis, and reported against a fixed set of sourced metrics throughout.",
    ],
    sections: [
      {
        eyebrow: "Discovery & audit",
        heading: "Cold start in a saturated category",
        body: [
          "Auto repair is one of the most competitive local categories on the SERP, dominated by chains with decades of review volume and independent shops that have already put in years of local SEO work. Carage Auto had all the ingredients of a cold-start problem: thin backlinks, no schema, weak local signals, and service pages targeting broad head terms with unrealistic ranking difficulty for a new entrant.",
          "There was nothing wrong with the business itself — the shop had strong reviews and a loyal local customer base — everything was wrong with how the internet could find it. The site was, in effect, invisible to the exact demand it was best positioned to serve.",
        ],
      },
      {
        eyebrow: "Information architecture",
        heading: "Rebuilding around commercial service clusters",
        body: [
          "First move: rebuild the information architecture around commercial service clusters — brake repair, oil change, transmission, diagnostics — each with a clear parent page and a deliberate internal-link model connecting spokes back to the cluster hub and to relevant location content.",
          "This gave every new piece of content a home before it was written, rather than bolting pages onto a flat, unstructured sitemap and hoping internal linking would sort itself out later.",
        ],
        bullets: [
          "Rebuilt IA around commercial service clusters",
          "Clear parent/spoke internal-link model",
        ],
      },
      {
        eyebrow: "On-page & technical remediation",
        heading: "On-page SEO overhaul and schema from day one",
        body: [
          "Second: a full on-page SEO overhaul — title tags, H1/H2 hierarchy, internal links, and AutoRepair plus LocalBusiness schema on every relevant page — built to spec from the start rather than retrofitted, which is materially cheaper and faster than fixing an established site's on-page debt.",
          "Fourth in sequence (after the gap analysis below): a Core Web Vitals pass addressing LCP and CLS issues that were modest in absolute terms but still enough to matter in a category where competitors are increasingly technically competent.",
        ],
        bullets: [
          "AutoRepair + LocalBusiness schema on every service page",
          "CWV pass: LCP + CLS improvements",
        ],
      },
      {
        eyebrow: "Content programme",
        heading: "A Semrush-driven gap analysis instead of a generic calendar",
        body: [
          "Third: a Semrush-driven competitive gap analysis to surface long-tail auto repair keywords with achievable difficulty, rather than chasing the same head terms every established competitor already owns. Two hundred-plus target keywords were mapped, each assigned to a specific content brief tied to a specific ranking opportunity — not a generic 'write about cars' calendar.",
          "This discipline matters most in a cold-start engagement, where every piece of content has to earn its ranking from zero authority; targeting realistic, winnable difficulty is the difference between visible progress in month three and a flat line for the first year.",
        ],
        bullets: [
          "Semrush-driven gap analysis (200+ target keywords)",
          "Every brief mapped to a specific ranking opportunity",
        ],
      },
      {
        eyebrow: "Local visibility",
        heading: "GBP overhaul and the local pack from zero",
        body: [
          "The Google Business Profile overhaul covered categories, services, geo-tagged photos, and a weekly posting cadence — the same fundamentals as our other local-services engagements, but starting from a genuinely blank baseline with no review or engagement history to build on, which meant early wins had to come from configuration accuracy and content freshness rather than accumulated trust signals.",
        ],
        bullets: [
          "GBP: categories, services, geo-tagged photos, weekly posts",
        ],
      },
      {
        eyebrow: "Digital PR & authority",
        heading: "Earning the first 209 referring domains",
        body: [
          "With no existing authority to leverage, early link acquisition focused on foundational citations (automotive directories, local business listings, chamber-of-commerce style associations) before moving into more selective digital PR once the site had enough indexed, quality content to be worth linking to.",
          "By the end of the engagement the referring-domain count had reached 209 — a modest but genuinely earned base for a cold-start brand, and one built entirely on relevant, category-appropriate sources rather than volume for its own sake.",
        ],
      },
      {
        eyebrow: "The AI-search dividend",
        heading: "137 pages cited across AI answers",
        body: [
          "A useful signal from the Semrush AI Search data: 137 pages on carageauto.com are now being cited across AI-generated answers. That's a compounding trust surface — one we build for deliberately with clear entity signalling, schema, and citation-friendly content structures rather than treating it as a side effect.",
          "Because the site was built with this in mind from month one — direct-answer paragraphs, consistent entity naming, structured FAQ blocks — it reached AI-citation surfaces faster than a retrofit on an established site typically would.",
        ],
      },
      {
        eyebrow: "Measurement setup",
        heading: "Proving progress with no historical baseline",
        body: [
          "Cold-start engagements need a different measurement posture, because there's no prior-period baseline to compare against. We used Semrush's keyword and authority tracking as the primary lens, supplemented by GSC for click ground truth, and reported cumulative progress against the 200+ keyword gap-analysis target list rather than a percentage-change metric that would have been meaningless in month one.",
        ],
      },
      {
        eyebrow: "What we'd do differently",
        heading: "Honest retrospective",
        body: [
          "The digital PR and citation-building phase could have started a few weeks earlier in parallel with the on-page work — link acquisition timelines are long enough that even foundational citations benefit from starting as soon as the site has any indexed content, rather than waiting for the full page set to launch.",
          "We'd also formalise the AI-citation tracking from week one rather than discovering it as a mid-engagement signal; had we set the baseline earlier, we'd have a cleaner before/after picture of exactly when and why citations began accumulating.",
        ],
      },
      {
        eyebrow: "Timeline",
        heading: "Six months, month by month",
        body: [
          "Month 1: IA rebuild and content cluster mapping; no live content changes yet.",
          "Month 2: on-page SEO overhaul begins; schema deployed as pages ship.",
          "Month 3: Semrush gap analysis complete; content briefs assigned; GBP overhaul begins.",
          "Month 4: content production ramps; foundational citation building begins.",
          "Month 5: CWV pass complete; digital PR outreach begins on the back of published content.",
          "Month 6: full reconciliation — 2.6K ranking keywords, 1.9K monthly visits, 209 referring domains, 137 AI-cited pages.",
        ],
      },
      {
        eyebrow: "The result",
        heading: "A defensible organic channel",
        body: [
          "2.6K ranking keywords. 1.9K monthly organic visits. 209 referring domains. And an authority profile that will keep compounding as long as we keep publishing and earning links against a foundation that was built correctly from the first month rather than patched together after the fact.",
        ],
      },
      {
        eyebrow: "What this means for you",
        heading: "If your business looks like this one",
        body: [
          "If you're starting from genuinely zero organic visibility, resist the temptation to chase the same head terms your established competitors already own. A disciplined gap analysis targeting realistic difficulty, paired with correct technical and schema foundations from day one, compounds faster than a broad, unfocused content push — and it gives you a defensible position rather than a temporary traffic spike.",
        ],
      },
    ],
    outcome: [
      "1.9K monthly organic visits",
      "2.6K ranking keywords",
      "209 referring domains",
      "137 AI-cited pages",
    ],
    readingTime: "12 min read",
  },
];

export const getCaseStudy = (slug: string) =>
  CASE_STUDIES.find((c) => c.slug === slug);
